/**
 * @carn/copy — dual-register strings and the copy-law linter.
 *
 * Authorised by implementation-brief §12 (`packages/copy` (dual-register strings)).
 *
 * Usage:
 *   const t = makeTranslator(literalMode ? 'literal' : 'warm');
 *   t('home.coolingRow', { count: 3 })
 *
 * Adding a string: add it to ./catalogue.ts with BOTH registers. The linter runs in
 * CI over the whole catalogue, so a string that breaks a copy law fails the build
 * rather than shipping and being caught in review.
 */

export * from './registers';
export * from './catalogue';
export * from './lint';
// Locale data rather than strings: the words that carry no information about WHICH job a
// captured line describes. Duplicate detection needs it; see ./stopwords.ts for why it is
// here and not in the screen.
export * from './stopwords';
// Locale data, same precedent (DEVIATIONS.md D18): the nouns that say "this is a thing you buy",
// and which aisle it is in. Read the header before adding a word — the rule about what stays OUT
// is the whole design (RULINGS R23).
export * from './shoppingWords';
// Locale data, same precedent again (RULINGS R165): the head verbs that say a captured line is a
// couple of minutes on your feet, which is the evidence the energy-fit proposal rests on. Read its
// header before adding a word — what stays OUT is the whole design, and this is the most
// speculative table in the product.
export * from './smallJobWords';
// Locale data, same precedent again (product-concept §7.2, §2 axiom 1; docs/DEVIATIONS.md
// §retrospective-capture RT1): the head phrases that say a captured line was written about work
// that had ALREADY started — "Finish painting the fence". Read its header before adding a phrase;
// as with the two tables above, what stays OUT is the whole design.
export * from './resumptionWords';
// The one answer to "what is an unnamed loop called". A leaf-package home so `packages/ui`,
// `apps/mobile`'s surfaces and its pure heuristics module can all reach it (RULINGS R27).
export * from './loopTitle';
// The one answer to "does this loop get resumed or started". Same leaf-package argument as
// ./loopTitle.ts: three surfaces need it and none of them may hold its own copy of the rule
// (docs/DEVIATIONS.md top/D38).
export * from './reentry';
// The resolved key→string map a widget process is handed, because a widget process cannot import
// this package (docs/specs/widgets-architecture.md §3 law 1). Read its header before adding a key
// by hand: the selection is a PREFIX over the catalogue on purpose.
export * from './widgetStrings';

import { type CopyKey, copy } from './catalogue';
import { type Register, fill, pick } from './registers';

export type Translator = <K extends CopyKey>(
  key: K,
  values?: Record<string, string | number>,
) => string;

export function makeTranslator(register: Register): Translator {
  return (key, values) => {
    const entry = copy[key];
    // `pick` needs the values, not just the register: an entry with a `count` slot
    // selects its singular form from them. Passing the register alone is how an app
    // ends up rendering "1 items remaining" (critique 5.3).
    const template = pick(entry, register, values);
    return values ? fill(template, values) : template;
  };
}

/** The register a given literal-mode setting selects. */
export function registerFor(literalMode: boolean): Register {
  return literalMode ? 'literal' : 'warm';
}
